import { Button } from '@/components/ui/button';

const time = (value) => value ? new Date(value).toLocaleString('en-NZ') : 'Not recorded';
const reason = (value) => (value || 'review_required').replaceAll('_', ' ');

export default function XeroSalesHealth({ report, busy, onRefresh, onRetry, onReview, onInspect, details }) {
  if (!report) return null;
  const running = report.state === 'running';
  const waiting = report.retry_at && new Date(report.retry_at).getTime() > Date.now();
  const blocked = busy || running || waiting;
  const incomplete = ['partial', 'failed'].includes(report.state);
  return <section className="mt-4 space-y-3 text-xs" aria-label="Sales sync health">
    <div className="flex justify-between gap-3 items-center">
      <div>
        <div className="font-semibold">Sync state: {report.state || 'idle'}</div>
        <div className="text-zinc-500">Last successful full sync: {time(report.last_success_at)}</div>
      </div>
      <Button size="sm" variant="outline" onClick={() => onRefresh(report.attention_page || 1)}>Refresh status</Button>
    </div>
    {incomplete && <div role="alert" className="border border-amber-300 bg-amber-50 text-amber-900 p-3">
      The latest sync is incomplete. Review the outcomes below; local changes are preserved when a conflict is detected.
    </div>}
    {waiting && <p role="status">Xero has limited requests. Retry after {time(report.retry_at)}.</p>}
    <div className="border border-zinc-200 p-3 space-y-2">
      <div className="font-semibold">Records requiring attention: {report.attention_count || 0}</div>
      {(report.attention || []).map(row => <div key={`${row.kind}:${row.id}`} className="flex items-center justify-between gap-3 border-t pt-2">
        <div>
          <a className="underline" href={`/app/${row.kind === 'quote' ? 'quotes' : 'customer-invoices'}/${encodeURIComponent(row.id)}`}>
            {row.kind} {row.number || row.id}
          </a>
          <div className="text-zinc-500">{reason(row.category)} · {row.state || 'pending'}</div>
        </div>
        {row.retryable ? <Button size="sm" variant="outline" disabled={blocked} onClick={() => onRetry(row)}>Retry safely</Button>
          : onReview ? <Button size="sm" variant="outline" disabled={blocked} onClick={() => onReview(row)}>Review both versions</Button> : <span className="text-amber-700">Review both records</span>}
      </div>)}
      {(report.attention_count || 0) > 50 && <div className="flex gap-3 items-center">
        <Button size="sm" variant="outline" disabled={(report.attention_page || 1) <= 1} onClick={() => onRefresh(report.attention_page - 1)}>Previous records</Button>
        <span>Page {report.attention_page || 1}</span>
        <Button size="sm" variant="outline" disabled={(report.attention_page || 1) * 50 >= report.attention_count} onClick={() => onRefresh((report.attention_page || 1) + 1)}>Next records</Button>
      </div>}
      {(report.attention || []).some(row => row.retryable) && <p className="text-zinc-500">Uncertain writes are checked in Xero before any further action. Conflicts are never resolved by automatically replacing local changes.</p>}
    </div>
    <div className="border border-zinc-200 p-3">
      <div className="font-semibold mb-2">Recent sync history</div>
      {(report.history || []).length === 0 && <p className="text-zinc-500">No recorded runs yet.</p>}
      {(report.history || []).map(run => <div key={run.id} className="flex justify-between gap-3 border-t py-2">
        <div>
          <div>{time(run.started_at)} · {run.trigger} · <strong>{run.state}</strong></div>
          <div className="text-zinc-500">{run.outcomes?.succeeded || 0} succeeded · {run.outcomes?.failed || 0} failed · {run.outcomes?.conflict || 0} conflicts · {run.outcomes?.skipped || 0} skipped</div>
          {(run.category || run.errors?.length > 0) && <div className="text-amber-700">{[run.category, ...(run.errors || [])].filter(Boolean).map(reason).join(', ')}</div>}
        </div>
        <button className="underline" onClick={() => onInspect(run.id, 1)}>View outcomes</button>
      </div>)}
      {details && <div className="border-t pt-3 space-y-2" aria-label="Document sync outcomes">
        {(details.items || []).map(item => <div key={item.id}>{item.fieldlink_invoice_id ? <a className="underline" href={`/app/${item.kind === 'quote' ? 'quotes' : 'customer-invoices'}/${encodeURIComponent(item.fieldlink_invoice_id)}`}>{item.kind} {item.fieldlink_invoice_id}</a> : item.kind} · {item.direction} · {item.status}{item.category ? ` · ${reason(item.category)}` : ''}</div>)}
        <div className="flex items-center gap-3">
          <button className="underline disabled:opacity-40" disabled={details.page <= 1} onClick={() => onInspect(details.run_id, details.page - 1)}>Previous outcomes</button>
          <span>Page {details.page}</span>
          <button className="underline disabled:opacity-40" disabled={details.page * 100 >= details.total} onClick={() => onInspect(details.run_id, details.page + 1)}>Next outcomes</button>
        </div>
      </div>}
    </div>
  </section>;
}
